import { RequirePermission } from "@components/common/require-permission";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { BaseMemberIdProps } from "@custom-types/base-types";
import { Button, Dialog, useToast, Text } from "@flat-ui";
import { ActionButtonSx } from "@styles/common-styles";
import { MemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

export function RevokeMemberSessions({
  memberId,
}: BaseMemberIdProps): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isRevoking, setIsRevoking] = useState<boolean>(false);
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();

  async function revokeMemberSessions(): Promise<void> {
    trackEvent({
      name: MemberEvents.revokeMemberSessions,
    });

    setIsRevoking(true);
    try {
      await faroCoreApiClient.admin.revokeUserSessions({
        memberId,
      });
      openToast({
        title:
          "Member sessions revoked. Member needs to log in on all devices again.",
      });
      setIsDialogOpen(false);
    } catch (error) {
      handleErrorWithToast({
        id: `revoke-member-sessions-fetch-${Date.now().toString()}`,
        title: "Could not revoke member sessions",
        error,
      });
    } finally {
      setIsRevoking(false);
    }
  }

  return (
    <>
      <RequirePermission permission="member.revokeSessions">
        <Button
          sx={{ ...ActionButtonSx }}
          onClick={() => {
            trackEvent({
              name: MemberEvents.openRevokeMemberSessionsDialog,
            });
            setIsDialogOpen(true);
          }}
          variant="secondary"
        >
          Revoke member sessions
        </Button>
      </RequirePermission>

      <Dialog
        open={isDialogOpen}
        title="Revoke member sessions"
        onCancel={() => setIsDialogOpen(false)}
        onConfirm={revokeMemberSessions}
        showSpinner={isRevoking}
      >
        <Text variant="bodyL">
          Are you sure you want to revoke the member's sessions? This will
          invalidate all active sessions for this member and require them to log
          in again.
        </Text>
      </Dialog>
    </>
  );
}
